import Controller from '@ember/controller';
import { action } from '@ember/object';
import { trackedArray, trackedObject } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
// eslint-disable-next-line import-x/default
import containerSizing, {
  type ContainerSizing,
} from 'frontend-utils/decorators/container-sizing';
import { openUrl } from 'frontend-utils/utils/url/open-url';
import type { Event } from 'lumen/models';
import { EVENTS_STATUSES, type Status } from 'lumen/models/event';
import type HotelEventsRoute from 'lumen/routes/hotel/events';
import type AjaxService from 'lumen/services/ajax';
import type { Day } from 'lumen/services/repositories/day';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import { getPeriodsInRange } from 'lumen/utils/dates/moment-helpers';
import { formatUrl } from 'lumen/utils/rates-request';
import type { ModelFrom } from 'lumen/utils/routes';

export interface SearchResults {
  events: Event[];
  loading: boolean;
  total: number;
}

interface AutoCompleteEvent extends Event {
  are_exact_coordinates: boolean;
  class: string;
  end_date: string;
  id: string;
  is_cancelled: boolean;
  name: string;
  start_date: string;
  status: Status;
  visitors: number;
}

interface EventsRequest {
  events: AutoCompleteEvent[];
  meta: { count: number };
}

// @ts-expect-error: Interface 'HotelEventsController' incorrectly extends interface 'Controller<unknown>'.
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
interface HotelEventsController extends ContainerSizing, Controller {}

@containerSizing({ headerHeight: 50 })
// eslint-disable-next-line @typescript-eslint/no-unsafe-declaration-merging
class HotelEventsController extends Controller {
  // @ts-expect-error: Type 'HotelEventsRoute' does not satisfy the constraint 'Route<unknown>'.
  declare model: ModelFrom<HotelEventsRoute>;
  // @ts-expect-error: Property 'queryParams' in type 'HotelEventsController' is not assignable to the same property in base type 'Controller<unknown>'.
  queryParams = [
    { eventsStatuses: { scope: 'controller' } },
    { eventsCategories: { scope: 'controller' } },
    'refresh',
    { month: { scope: 'controller' } },
    { highlight: { scope: 'controller' } },
  ] as const;

  @service declare ajax: AjaxService;
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service('repositories/day')
  declare repositoriesDay: Services['repositories/day'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];

  @tracked eventsCategories = [
    ...DEFAULT_QP_VALUES.hotel['events'].eventsCategories,
  ] as string[];
  @tracked eventsStatuses = [
    ...DEFAULT_QP_VALUES.hotel['events'].eventsStatuses,
  ] as string[];
  @tracked month = '';
  @tracked refresh = false;
  @tracked searchResults = trackedObject<SearchResults>({
    events: trackedArray(),
    loading: false,
    total: 0,
  });

  highlight: string | null = null;

  get dateRange(): { end: Dayjs; start: Dayjs } {
    return {
      start: this.dateRangeStart,
      end: this.dateRangeEnd,
    };
  }

  get dateRangeEnd(): Dayjs {
    return this.monthMoment.endOf('month');
  }

  get dateRangeStart(): Dayjs {
    return this.monthMoment.startOf('month');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get exportChoices() {
    return [
      {
        groupName: this.intl.t('general.export_group.export'),
        options: [
          {
            id: 'defined_report',
            label: this.intl.t('general.next_days', { count: 365 }),
            selection: 365,
          },
          { id: 'defined_report', label: this.intl.t('rates.active_month') },
        ],
      },
    ];
  }

  get isLoading(): boolean {
    const { events, holidays } = this.model;

    return Boolean(events.isRunning || holidays.isRunning);
  }

  get monthMoment(): Dayjs {
    return this.hotelAndMarketService.toLocalTime(`${this.month}-15`);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get periods() {
    if (this.isLoading) {
      // Dummy days are pre-created in the route's model() hook to avoid
      // store.createRecord() calls inside a reactive computation (Glimmer
      // autotracking violation). Here we only peek — no writes.
      const dummyDays: Day[] = [];

      getPeriodsInRange(
        this.dateRangeStart.startOf('week'),
        this.dateRangeEnd.endOf('week'),
        'day',
      ).forEach((period) => {
        const dummyDay = this.repositoriesDay.peekByDate(period.format());
        if (dummyDay) {
          dummyDays.push(dummyDay);
        }
      });

      return dummyDays;
    }

    return this.model?.events.value;
  }

  async sendRequest(
    input: string,
    page: number,
    numEventsToShow = 10,
  ): Promise<SearchResults> {
    const searchResults: SearchResults = {
      events: trackedArray(),
      loading: true,
      total: 0,
    };

    try {
      const result = await this.ajax.request('/autocomplete/events/', {
        method: 'GET',
        data: {
          hotel_id: this.hotelAndMarketService.hotel?.id,
          q: input,
          page,
        },
      });

      const typedResult = result as EventsRequest;
      const events = typedResult.events.slice(0, numEventsToShow);

      events.forEach((event) => {
        event.startDate = dayjs.utc(event.start_date, 'YYYY-MM-DD');
        event.endDate = dayjs.utc(event.end_date, 'YYYY-MM-DD');
        event.numberOfDays = event.startDate.diff(event.endDate, 'day') + 1;
        event.areExactCoordinates = event.are_exact_coordinates;
        event.isCancelled = event.is_cancelled;

        if (event.status === EVENTS_STATUSES.ACCEPTED) {
          event.class = 'checkmark';
        } else if (event.status === EVENTS_STATUSES.DECLINED) {
          event.class = 'cross';
        }

        // @ts-expect-error: Incorrect type
        searchResults.events.push(event);
      });

      searchResults.total = Math.ceil(typedResult.meta.count / 10);
    } catch (error) {
      this.errorTracking.captureException(error);
    } finally {
      searchResults.loading = false;

      // eslint-disable-next-line no-unsafe-finally
      return searchResults;
    }
  }

  @action
  addEvent(): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo('hotel.events.edit');
  }

  @action
  addEventForDate(startDate: string): void {
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo('hotel.events.edit', {
      queryParams: { initialDate: startDate },
    });
  }

  @action
  addExistingEvents(event: { added: boolean; id: string }): void {
    const status = event.added ? 'accepted' : 'rejected';
    this.ajax
      .request(`/events/${event.id}`, {
        type: 'put',
        data: JSON.stringify({ event: { status } }),
      })
      .then(() => {
        this.store.unloadAll('yearoverview');
      })
      .catch((/* xhr, errorStatus, error */) => {
        // console.error(`${errorStatus} : ${error}`);
      });
  }

  @action
  exportEvents(exportChoice: { selection: number | null }): void {
    const params = {
      hotel_id: this.hotelAndMarketService.hotel?.id,
      from_date_range_start: '',
      from_date_range_end: '',
    };

    if (exportChoice.selection) {
      const start = this.hotelAndMarketService.getLocalTime();
      params.from_date_range_start = start.format('YYYY-MM-DD');
      params.from_date_range_end = start
        .add(exportChoice.selection, 'day')
        .format('YYYY-MM-DD');
    } else {
      params.from_date_range_start = this.dateRangeStart.format('YYYY-MM-DD');
      params.from_date_range_end = this.dateRangeEnd.format('YYYY-MM-DD');
    }

    const url = formatUrl(
      '/apigateway/v1/app/download/events',
      params,
      this.ajax.host,
    );
    openUrl(url);
  }

  @action
  async fetchSearchResult(input: string, page: number = 1): Promise<void> {
    this.searchResults = trackedObject({
      events: trackedArray(),
      loading: true,
      total: 0,
    });

    this.searchResults = await this.sendRequest(input, page);
  }

  @action
  resetEvents(): void {
    // this.store.unloadAll('period');
    this.store.unloadAll('day');
    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    this.router.legacyTransitionTo('hotel.events', {
      queryParams: { refresh: true },
    });
  }

  @action
  selectionsChanged(selection: string, value: string[]): void {
    if (selection === 'eventsFilter') {
      this.eventsStatuses = value;
    }
    if (selection === 'eventsCategories') {
      this.eventsCategories = value;
    }
  }

  @action
  updateSelectedMonth(selectedRange: { end: Dayjs; start: Dayjs }): void {
    if (selectedRange) {
      this.month = selectedRange.start.format('YYYY-MM');
    }
  }
}

export default HotelEventsController;
